import { useState, useEffect, useCallback } from 'react';
import { ordersAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ShoppingCart, Search, MoreVertical, RefreshCw, XCircle, Download } from 'lucide-react';
import { formatRemaining, getDownloadStatus } from '../lib/downloadStatus';

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const loadOrders = useCallback(async () => {
    try {
      const res = await ordersAPI.list({ page, limit: 50 });
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handleReissue = async (orderId) => {
    try {
      const res = await ordersAPI.reissueLink(orderId);
      toast.success('Download link reissued');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reissue link');
    }
  };

  const handleRevoke = async (orderId) => {
    if (!window.confirm('Revoke download access for this order?')) return;
    try {
      await ordersAPI.revoke(orderId);
      toast.success('Downloads revoked');
      loadOrders();
    } catch (err) {
      toast.error('Failed to revoke');
    }
  };

  const filtered = orders.filter(o =>
    o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.product_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.ref?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="space-y-4"><div className="h-10 w-48 bg-muted animate-pulse rounded" /><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;
  }

  return (
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} order{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {orders.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShoppingCart className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold">No orders yet</h3>
            <p className="text-sm text-muted-foreground mt-1">Orders will appear here when customers purchase your products</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <Card>
            <Table data-testid="orders-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead>Validity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(order => {
                  const dl = order.download_info;
                  const accessStatus = getDownloadStatus(dl);
                  return (
                    <TableRow key={order.id}>
                      <TableCell className="font-mono text-xs">{order.ref}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{order.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{order.product_name}</p>
                          <p className="text-xs text-muted-foreground">{order.product_type}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">RM {(order.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant={order.status === 'paid' ? 'default' : order.status === 'cancelled' ? 'destructive' : 'secondary'}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {dl ? (
                          <div className="flex items-center gap-1">
                            <Download className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{dl.downloads_used}/{dl.max_downloads}</span>
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`order-validity-${order.id}`}>
                        {dl ? formatRemaining(dl.expiry_at) : '-'}
                      </TableCell>
                      <TableCell data-testid={`order-access-status-${order.id}`}>
                        {accessStatus.tone === 'danger' ? (
                          <Badge variant="destructive" className="text-[10px]">{accessStatus.label}</Badge>
                        ) : accessStatus.tone === 'muted' ? (
                          <Badge variant="secondary" className="text-[10px]">{accessStatus.label}</Badge>
                        ) : accessStatus.tone === 'success' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">{accessStatus.label}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleReissue(order.id)}>
                              <RefreshCw className="h-4 w-4 mr-2" /> Reissue Link
                            </DropdownMenuItem>
                            {order.status !== 'cancelled' && (
                              <DropdownMenuItem onClick={() => handleRevoke(order.id)} className="text-destructive">
                                <XCircle className="h-4 w-4 mr-2" /> Revoke Access
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
